/*
  Cart drawer colour dropdown (snippets/cart-line-color.liquid).
  The list is a popover (top layer): it floats over the cart, so the rows below never move. It opens under the
  button (or above it when there is no room), closes on outside tap / Esc / scrolling the cart. The drawer shows these
  products one row per item (sections/cart-drawer.liquid), so picking a colour moves that row's 1 item (Black x2 ->
  Black x1 + Mint x1) in one /cart/update.js request (old colour down 1, new colour up 1), then re-renders the drawer.
  Changes run one at a time in the order picked, each on the line as the cart has it by then (discounts can change line
  keys, and the drawer doesn't always redraw this dropdown).
  Row order: the cart lists a newly added colour first, so a changed row would jump to the top. The drawer keeps its own
  row order (one variant id per row, in localStorage) and shows the rows in it after every redraw: a changed row stays
  where it was, an extra item of a colour goes after that colour's rows, a new product goes on top. Rows are ordered
  with CSS `order` (never moved in the page: moving them would re-attach the theme's quantity buttons twice).
*/
(() => {
  if (window.cartLineColor) return;
  window.cartLineColor = true;

  const root = () => ((window.theme && theme.routes && theme.routes.root_url) || '/').replace(/\/$/, '');
  const GAP = 6;
  // Colour changes run one after another, each on the cart as it is by then.
  let queue = Promise.resolve();

  // ---------- Row order ----------
  const ORDER_KEY = 'venty:cart-row-order';
  let memoryOrder = [];
  const loadOrder = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(ORDER_KEY) || '[]');
      return Array.isArray(saved) ? saved.map(String) : [];
    } catch (e) {
      return memoryOrder.slice();
    }
  };
  const saveOrder = (order) => {
    memoryOrder = order.slice();
    try {
      localStorage.setItem(ORDER_KEY, JSON.stringify(order));
    } catch (e) {
      /* private mode etc.: keep it for this page only */
    }
  };
  // The drawer's rows as shown (CSS order), not as they sit in the page.
  const drawerRows = () => {
    const rows = Array.from(document.querySelectorAll('cart-drawer cart-items li[id^="CartDrawer-Item-"]'));
    const shown = (row, index) => (row.style.order === '' ? index : Number(row.style.order));
    return rows
      .map((row, index) => ({ row, at: shown(row, index) }))
      .sort((a, b) => a.at - b.at)
      .map((entry) => entry.row);
  };
  // After a colour change / remove the saved order already describes the cart to come: leave it alone until the drawer
  // has been redrawn (a new list element).
  let pending = false;
  let lastList = null;

  const applyOrder = () => {
    const rows = Array.from(document.querySelectorAll('cart-drawer cart-items li[id^="CartDrawer-Item-"]'));
    if (!rows.length) return;
    const list = rows[0].parentElement;
    if (pending && list === lastList) return;
    pending = false;
    lastList = list;
    const pool = new Map();
    rows.forEach((row) => {
      const id = String(row.dataset.variantId);
      if (!pool.has(id)) pool.set(id, []);
      pool.get(id).push(row);
    });
    // Rows in the remembered order (a colour that now has fewer items just loses its last places)...
    const ordered = [];
    loadOrder().forEach((id) => {
      const free = pool.get(id);
      if (free && free.length) ordered.push(free.shift());
    });
    // ...then the rest: another item of a colour already listed goes after that colour's rows, anything else on top.
    const top = [];
    rows.forEach((row) => {
      if (ordered.includes(row)) return;
      const id = String(row.dataset.variantId);
      let last = -1;
      ordered.forEach((other, index) => {
        if (String(other.dataset.variantId) === id) last = index;
      });
      if (last >= 0) ordered.splice(last + 1, 0, row);
      else top.push(row);
    });
    const final = top.concat(ordered);
    list.classList.add('cart-rows-ordered');
    final.forEach((row, index) => {
      row.style.order = String(index);
      row.toggleAttribute('data-row-first', index === 0);
      row.toggleAttribute('data-row-last', index === final.length - 1);
    });
    saveOrder(final.map((row) => String(row.dataset.variantId)));
  };

  // After every redraw of the drawer (and once on load). Runs before the browser paints, so rows never visibly jump.
  const watchDrawer = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    applyOrder();
    new MutationObserver(applyOrder).observe(drawer, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchDrawer);
  else watchDrawer();

  // The row at `position` changed colour: remember the new colour in that place.
  const rememberSwap = (position, oldVariant, newVariant) => {
    const order = loadOrder();
    let index = order[position] === oldVariant ? position : -1;
    if (index < 0) {
      // Not where expected (the order changed meanwhile): use the nearest row of the old colour.
      let best = Infinity;
      order.forEach((id, i) => {
        if (id === oldVariant && Math.abs(i - position) < best) {
          best = Math.abs(i - position);
          index = i;
        }
      });
    }
    if (index < 0) return;
    order[index] = newVariant;
    saveOrder(order);
    pending = true;
  };

  // Remove takes away the row that was clicked (not just the last row of that colour).
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element && event.target.closest('cart-drawer a[is="cart-remove-button"]');
      if (!link) return;
      const row = link.closest('li');
      const position = drawerRows().indexOf(row);
      const order = loadOrder();
      if (position < 0 || order[position] !== String(row.dataset.variantId)) return;
      order.splice(position, 1);
      saveOrder(order);
      pending = true;
    },
    true
  );

  const place = (menu) => {
    const trigger = document.querySelector(`[popovertarget="${CSS.escape(menu.id)}"]`);
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.max(rect.width, 220);
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    menu.style.width = `${width}px`;
    menu.style.left = `${left}px`;
    const height = menu.offsetHeight;
    const below = window.innerHeight - rect.bottom - GAP - 8;
    const openUp = height > below && rect.top - GAP - 8 > below;
    menu.style.top = `${openUp ? rect.top - GAP - height : rect.bottom + GAP}px`;
    menu.classList.toggle('is-up', openUp);
    trigger.setAttribute('aria-expanded', 'true');
  };

  // Position on open; clear state on close.
  document.addEventListener(
    'toggle',
    (event) => {
      const menu = event.target;
      if (!(menu instanceof HTMLElement) || !menu.classList.contains('cart-color__menu')) return;
      const trigger = document.querySelector(`[popovertarget="${CSS.escape(menu.id)}"]`);
      if (event.newState === 'open') {
        place(menu);
        const selected = menu.querySelector('[aria-selected="true"]') || menu.querySelector('.cart-color__option:not(:disabled)');
        if (selected) selected.focus({ preventScroll: true });
      } else if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    },
    true
  );

  // An open list would detach from its button when the cart scrolls or the window resizes: close it.
  const closeOpen = () => {
    document.querySelectorAll('.cart-color__menu:popover-open').forEach((menu) => menu.hidePopover());
  };
  document.addEventListener('scroll', (event) => {
    if (event.target instanceof Element && event.target.closest('.cart-color__menu')) return;
    closeOpen();
  }, true);
  window.addEventListener('resize', closeOpen);

  // Arrow keys move through the colours.
  document.addEventListener('keydown', (event) => {
    const option = event.target instanceof Element && event.target.closest('.cart-color__option');
    if (!option || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    const options = Array.from(option.parentElement.querySelectorAll('.cart-color__option:not(:disabled)'));
    const next = options[(options.indexOf(option) + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length];
    if (next) next.focus();
  });

  document.addEventListener('click', async (event) => {
    const option = event.target instanceof Element && event.target.closest('.cart-color__option');
    if (!option || option.disabled) return;
    const menu = option.closest('.cart-color__menu');
    const wrap = option.closest('[data-cart-color]');
    if (!menu || !wrap) return;

    if (option.getAttribute('aria-selected') === 'true') {
      menu.hidePopover();
      return;
    }
    // A row that is already changing ignores another pick until the cart redraws it, so one fan is never moved twice.
    // Picks on other rows wait their turn (nothing is dropped).
    if (wrap.hasAttribute('data-busy')) return;

    const line = wrap.dataset.line;
    const oldVariant = String(wrap.dataset.variantId || '');
    const position = drawerRows().indexOf(wrap.closest('li'));
    const error = menu.querySelector('[data-cart-color-error]');
    wrap.setAttribute('data-busy', '');
    menu.classList.add('is-busy');
    option.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    queue = queue.then(() => swap(wrap, menu, option, error, line, oldVariant, position));
  });

  async function swap(wrap, menu, option, error, line, oldVariant, position) {
    let done = false;
    try {
      // The line as the cart has it right now. The drawer doesn't always redraw this dropdown after a cart change (e.g. a
      // merge), so its stored key and quantity can be out of date, and discounts can change keys: find it by key, else
      // by variant, and read its quantity from the cart.
      const cart = await (await fetch(`${root()}/cart.js`, { headers: { Accept: 'application/json' } })).json();
      const before = cart.items.find((item) => item.key === line) || cart.items.find((item) => String(item.variant_id) === oldVariant);
      if (!before) {
        if (menu.matches(':popover-open')) menu.hidePopover();
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
        return;
      }
      const newVariant = String(option.dataset.variantId);
      const alreadyInCart = cart.items
        .filter((item) => String(item.variant_id) === newVariant)
        .reduce((sum, item) => sum + item.quantity, 0);

      // One request moves the row's item: old colour down 1, new colour up 1 (joining that colour when it's in the cart).
      // Nothing is left half-done, and a sold-out colour fails without changing the cart.
      const response = await fetch(`${root()}/cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: { [String(before.variant_id)]: before.quantity - 1, [newVariant]: alreadyInCart + 1 } }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        if (error) {
          error.textContent = result.description || result.message || 'That color is not available. Try another.';
          error.hidden = false;
        }
        return;
      }
      done = true;
      // Keep the row in its place: the redraw (below) is put back into this order.
      if (position >= 0) rememberSwap(position, String(before.variant_id), newVariant);
      if (menu.matches(':popover-open')) menu.hidePopover();
      // Re-render the cart (drawer) and let the rest of the theme (cart count, etc.) know the cart changed.
      if (window.theme && theme.pubsub) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-line-color', cart: result });
      }
      if (document.querySelector('cart-drawer')) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
      } else {
        window.location.reload();
      }
    } catch (err) {
      console.error(err);
    } finally {
      menu.classList.remove('is-busy');
      option.removeAttribute('aria-busy');
      // After a change the redraw replaces this row; if it hasn't (yet), free it again after a moment.
      if (done) setTimeout(() => wrap.removeAttribute('data-busy'), 4000);
      else wrap.removeAttribute('data-busy');
    }
  }
})();
